<script setup lang="ts">
import { computed, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { Activity, Bot, Clock3, Save, Sparkles } from 'lucide-vue-next'

import type { AnalysisSettings, MonitorStatus } from '@/types/monitor'

const props = defineProps<{
  settings: AnalysisSettings | null
  status: MonitorStatus
  saving: boolean
}>()

const emit = defineEmits<{
  save: [settings: AnalysisSettings]
}>()

const { t } = useI18n()
const draft = ref<AnalysisSettings>({
  enabled: true,
  interval_seconds: 1800,
  batch_trigger_enabled: true,
  batch_size: 100,
  max_messages: 500,
  system_prompt: '',
  prompt_template: '',
})

watch(
  () => props.settings,
  (settings) => {
    if (settings) draft.value = { ...settings }
  },
  { immediate: true, deep: true },
)

const intervalMinutes = computed({
  get: () => Math.max(1, Math.round(draft.value.interval_seconds / 60)),
  set: (value: number) => {
    draft.value.interval_seconds = Math.max(1, Number(value) || 1) * 60
  },
})

function save(): void {
  emit('save', { ...draft.value })
}
</script>

<template>
  <article class="tm-card tm-settings-card tm-analysis-policy-card">
    <header class="tm-section-title">
      <div><Activity :size="18" /><div><strong>{{ t('analysisSettings.policyTitle') }}</strong><span>{{ t('analysisSettings.policyHint') }}</span></div></div>
    </header>
    <div class="tm-settings-section tm-settings-section--switch">
      <div>
        <h2>{{ t('analysisSettings.enabled') }}</h2>
        <p>{{ t('analysisSettings.intervalHelp') }}</p>
      </div>
      <el-switch v-model="draft.enabled" size="large" />
    </div>

    <div class="tm-form-grid">
      <label class="tm-field">
        <span><Clock3 :size="16" />{{ t('analysisSettings.interval') }}</span>
        <div class="tm-number-field"><input v-model.number="intervalMinutes" type="number" min="1" max="10080" /><em>min</em></div>
      </label>
      <label class="tm-field">
        <span>{{ t('analysisSettings.maxMessages') }}</span>
        <input v-model.number="draft.max_messages" type="number" min="1" max="5000" />
      </label>
    </div>

    <div class="tm-settings-section tm-settings-section--switch tm-settings-section--compact">
      <div>
        <h3>{{ t('analysisSettings.batchTrigger') }}</h3>
        <p>{{ t('analysisSettings.pending', { count: status.analysis_pending_messages }) }}</p>
      </div>
      <el-switch v-model="draft.batch_trigger_enabled" />
    </div>
    <label v-if="draft.batch_trigger_enabled" class="tm-field">
      <span>{{ t('analysisSettings.batchSize') }}</span>
      <input v-model.number="draft.batch_size" type="number" min="1" :max="draft.max_messages" />
    </label>

    <details class="tm-analysis-prompt-details">
      <summary><Bot :size="16" /><span>{{ t('analysisSettings.promptEditor') }}</span><small>{{ t('analysisSettings.promptEditorHint') }}</small></summary>
      <div class="tm-analysis-prompt-fields">
        <label class="tm-field tm-field--stacked">
          <span><Bot :size="16" />{{ t('analysisSettings.systemPrompt') }}</span>
          <textarea v-model="draft.system_prompt" rows="6" />
        </label>
        <label class="tm-field tm-field--stacked">
          <span><Sparkles :size="16" />{{ t('analysisSettings.promptTemplate') }}</span>
          <textarea v-model="draft.prompt_template" rows="10" placeholder="{intro}\n\n{messages}" />
          <small>{{ t('analysisSettings.templateHelp', {
            messages: '{messages}',
            intro: '{intro}',
            n_messages: '{n_messages}',
            n_sources: '{n_sources}',
            historical_context: '{historical_context}',
            topics: '{topics}',
            stances: '{stances}',
            tones: '{tones}',
          }) }}</small>
        </label>
      </div>
    </details>

    <button class="tm-button tm-button--primary tm-button--save" :disabled="saving" @click="save">
      <Save :size="17" />{{ t('analysisSettings.save') }}
    </button>
  </article>
</template>
